/**
 * Upload Queue Hook
 *
 * Manages file uploads with progress tracking, error handling, and cancellation.
 */

import { useState, useCallback, useRef } from 'react';
import type { UploadItem } from '../../UI/Uploader/types';
import type { ZapworksProvider } from '../providers/zapworks/ZapworksProvider';
import { MediaType } from '../types';
import { parseGltfExternalRefs, convertGltfToGlb } from '../utils/gltf';
import { extractZipToFiles } from '../utils/zip';
import { isHiddenFile, getFileExtension, getMediaTypeFromExtension } from '../utils';
import { compressImageForUpload } from '../utils/image';
import { detect360, corresponding360Type } from '../utils/equirectangular';
import { IFile } from '@zappar/zw-api-client';
import { IAsset } from '../providers';

interface UseUploadQueueOptions {
  /** Zapworks provider instance for uploading */
  provider: ZapworksProvider;
  /** Workspace ID to associate uploaded files with */
  workspaceId?: string;
  /** Callback when an upload completes successfully */
  onUploadComplete?: (assetId: string) => void;
  /** Callback when an upload fails */
  onUploadError?: (fileName: string, error: string) => void;
  /** Callback when a new version of an existing asset is uploaded successfully */
  onNewVersionUploaded?: (asset: IAsset) => void;
  /**
   * When true, a file whose pixel dimensions are ~2:1 (equirectangular) but which
   * carries no 360 metadata is still classified as the corresponding 360 type.
   * The MediaLibrary sets this when the user is in a 360 context (a 360 tab is
   * open, or only 360 media types are enabled). Embedded metadata always wins.
   */
  classifyEquirectangularByAspectRatio?: boolean;
}

/**
 * Represents the state and operations for managing a media upload queue.
 *
 * @property uploads - The current list of items in the upload queue.
 * @property addFiles - Asynchronously adds new files to the queue with optional media type, workflow status, and previous file context.
 * @property addMissingFiles - Adds missing files to an existing upload item identified by its upload ID.
 * @property cancelUpload - Cancels an in-progress upload using its upload ID.
 * @property retryUpload - Retries a failed or stopped upload using its upload ID.
 * @property removeUpload - Removes a specific upload item from the queue by its upload ID.
 * @property clearCompleted - Clears all successfully uploaded items from the queue.
 * @property clearAll - Clears all items currently in the upload queue.
 */
export interface IUploadQueueData {
  uploads: UploadItem[];
  addFiles: (files: File[], mediaType?: MediaType, uploadWorkflowStatus?: string | null, prevFile?: IFile) => Promise<void>;
  addMissingFiles: (uploadId: string, newFiles: File[]) => void;
  cancelUpload: (uploadId: string) => void;
  retryUpload: (uploadId: string) => void;
  removeUpload: (uploadId: string) => void;
  clearCompleted: () => void;
  clearAll: () => void;
}

export const useUploadQueue = ({
  provider,
  workspaceId,
  onUploadComplete,
  onUploadError,
  onNewVersionUploaded,
  classifyEquirectangularByAspectRatio,
}: UseUploadQueueOptions): IUploadQueueData => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const activeUploadsRef = useRef<Set<string>>(new Set());
  // Held in a ref so the (memoised) upload callbacks always read the latest value
  // without being torn down and recreated when the user switches tab.
  const classifyByAspectRatioRef = useRef(false);
  classifyByAspectRatioRef.current = classifyEquirectangularByAspectRatio ?? false;

  /**
   * Add files to the upload queue and start uploading.
   * Detects .gltf files and checks for missing external resource references.
   */
  const addFiles = useCallback(
    async (files: File[], mediaType?: MediaType, uploadWorkflowStatus?: string | null, prevFile?: IFile) => {
      const fileArray = Array.from(files);
      const zipFiles = fileArray.filter((f) => f.name.toLowerCase().endsWith('.zip') || f.type === 'application/zip');
      const nonZipFiles = fileArray.filter((f) => !f.name.toLowerCase().endsWith('.zip') && f.type !== 'application/zip');

      // Extract contents from any zip files and merge into the processing pipeline
      const extractedFromZips: File[] = [];
      for (const zipFile of zipFiles) {
        try {
          const extracted = await extractZipToFiles(zipFile);
          extractedFromZips.push(...extracted.filter((f) => !isHiddenFile(f.name)));
        } catch (err) {
        }
      }
      const expandedFileArray = [...nonZipFiles, ...extractedFromZips];

      const gltfFiles = expandedFileArray.filter((f) => f.name.toLowerCase().endsWith('.gltf'));
      const otherFiles = expandedFileArray.filter((f) => !f.name.toLowerCase().endsWith('.gltf'));

      // Build a lookup of non-gltf files by basename (potential resources)
      // Keyed by bare filename so "textures/foo.png" refs match a dropped "foo.png"
      const resourcesByName = new Map(otherFiles.map((f) => [f.name, f]));

      const newUploads: UploadItem[] = [];
      const claimedResourceNames = new Set<string>();


      for (const gltfFile of gltfFiles) {
        const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        let externalRefs: string[] = [];

        try {
          externalRefs = await parseGltfExternalRefs(gltfFile);
        } catch (err) {
          newUploads.push({
            id,
            file: gltfFile,
            progress: 0,
            status: 'error' as const,
            error: `Invalid .gltf file`,
          });
          continue;
        }

        const foundResources: File[] = [];
        const missingFiles: string[] = [];

        for (const ref of externalRefs) {
          const resource = resourcesByName.get(ref) ?? resourcesByName.get(ref.split('/').pop()!);
          if (resource) {
            foundResources.push(resource);
            claimedResourceNames.add(resource.name);
          } else {
            missingFiles.push(ref);
          }
        }

        if (missingFiles.length > 0) {
          newUploads.push({
            id,
            file: gltfFile,
            progress: 0,
            status: 'error' as const,
            error: `Missing resource files`,
            missingFiles,
            gltfResources: foundResources,
          });
        } else {
          newUploads.push({
            id,
            file: gltfFile,
            progress: 0,
            status: 'pending' as const,
            gltfResources: foundResources,
          });
        }
      }

      // Upload non-gltf files that weren't claimed as resources
      for (const file of otherFiles) {
        if (claimedResourceNames.has(file.name)) continue;
        newUploads.push({
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file,
          progress: 0,
          status: 'pending' as const,
        });
      }

      setUploads((prev) => [...prev, ...newUploads]);

      // Start uploading pending items
      for (const upload of newUploads) {
        if (upload.status === 'pending') {
          uploadFile(upload, mediaType, uploadWorkflowStatus, workspaceId, prevFile);
        }
      }
    },
    [provider]
  );

  /**
   * Upload a single file
   */
  const uploadFile = useCallback(
    async (uploadItem: UploadItem, mediaType?: MediaType, uploadWorkflowStatus?: string | null, workspaceId?: string, prevFile?: IFile) => {
      const { id, file } = uploadItem;

      if (activeUploadsRef.current.has(id)) return;
      activeUploadsRef.current.add(id);

      const abortController = new AbortController();
      abortControllersRef.current.set(id, abortController);

      try {
        // Update status to uploading
        setUploads((prev) =>
          prev.map((u) =>
            u.id === id ? { ...u, status: 'uploading' as const, progress: 0 } : u
          )
        );

        // Create a progress callback to update the upload progress
        const progressCallback = (progress: number) => {
          setUploads((prev) =>
            prev.map((u) =>
              u.id === id ? { ...u, progress: Math.round(progress * 100) } : u
            )
          );
        };

        // Convert .gltf + resources to a single .glb before uploading
        const convertedFile =
          uploadItem.gltfResources !== undefined
            ? await convertGltfToGlb(file, uploadItem.gltfResources)
            : file;

        // Detect 360°/equirectangular content from the ORIGINAL file (must run
        // before compressImageForUpload, which strips the GPano XMP packet) so the
        // asset is registered (and wrapped) as a 360 type. Embedded metadata is
        // authoritative; a 2:1 aspect ratio only upgrades to 360 when the host has
        // opted in via a 360 context (classifyEquirectangularByAspectRatio).
        let detected360: MediaType.Image360 | MediaType.Video360 | null = null;
        if (!prevFile) {
          const signals = await detect360(file);
          detected360 =
            signals.metadataType ??
            (classifyByAspectRatioRef.current && signals.hasEquirectangularAspectRatio
              ? corresponding360Type(file)
              : null);
        }

        // Compress raster images before uploading to reduce storage and bandwidth
        const fileToUpload = await compressImageForUpload(convertedFile);

        // Upload using the Zapworks provider with progress tracking
        let asset;
        if (prevFile) {
          asset = await provider.uploadNewVersion(prevFile, fileToUpload);
          onNewVersionUploaded?.(asset);
        } else {
          asset = await provider.upload(
            fileToUpload,
            {
              name: fileToUpload.name,
              type: detected360 ?? mediaType,
              caption: file.name,
              workspaceId,
              workflowStatus: uploadWorkflowStatus,
            },
            progressCallback
          );
        }

        // Check if upload was cancelled
        if (abortController.signal.aborted) {
          throw new Error('Upload cancelled');
        }

        // Update status to completed
        setUploads((prev) =>
          prev.map((u) =>
            u.id === id
              ? {
                ...u,
                status: 'completed' as const,
                progress: 100,
                assetId: asset.getId(),
                showAssetLink: !prevFile, // Don't show asset link for new version uploads since the asset already exists
              }
              : u
          )
        );

        // Clean up abort controller
        abortControllersRef.current.delete(id);

        // Notify completion
        onUploadComplete?.(asset.getId());
      } catch (error) {
        // Check if upload was cancelled
        if (abortController.signal.aborted) {
          // Remove from queue
          setUploads((prev) => prev.filter((u) => u.id !== id));
          abortControllersRef.current.delete(id);
          return;
        }

        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed';

        // Update status to error
        setUploads((prev) =>
          prev.map((u) =>
            u.id === id
              ? { ...u, status: 'error' as const, error: errorMessage }
              : u
          )
        );

        // Clean up abort controller
        abortControllersRef.current.delete(id);

        // Notify error
        onUploadError?.(file.name, errorMessage);
      } finally {
        activeUploadsRef.current.delete(id);
      }
    },
    [provider, onUploadComplete, onUploadError]
  );

  /**
   * Provide missing resource files for a stalled .gltf upload.
   * If all missing files are now resolved, starts the upload.
   */
  const addMissingFiles = useCallback(
    (uploadId: string, newFiles: File[]) => {
      setUploads((prev) => {
        const upload = prev.find((u) => u.id === uploadId);
        if (!upload || !upload.missingFiles) return prev;

        const newFilesByName = new Map(newFiles.map((f) => [f.name, f]));
        const allResources = [...(upload.gltfResources ?? [])];
        const stillMissing: string[] = [];

        for (const ref of upload.missingFiles) {
          const file = newFilesByName.get(ref) ?? newFilesByName.get(ref.split('/').pop()!);
          if (file) {
            allResources.push(file);
          } else {
            stillMissing.push(ref);
          }
        }

        if (stillMissing.length > 0) {
          return prev.map((u) =>
            u.id === uploadId
              ? {
                  ...u,
                  missingFiles: stillMissing,
                  gltfResources: allResources,
                  error: `Missing resource files`,
                }
              : u
          );
        }

        // All resources resolved — start upload
        const resolvedUpload: UploadItem = {
          ...upload,
          status: 'pending' as const,
          error: undefined,
          missingFiles: undefined,
          gltfResources: allResources,
        };

        // Kick off upload outside of this setState call
        setTimeout(() => uploadFile(resolvedUpload, MediaType.Model3D, null, workspaceId), 0);

        return prev.map((u) => (u.id === uploadId ? resolvedUpload : u));
      });
    },
    [uploadFile]
  );

  /**
   * Cancel an upload
   */
  const cancelUpload = useCallback((uploadId: string) => {
    const controller = abortControllersRef.current.get(uploadId);
    if (controller) {
      controller.abort();
    }
  }, []);

  /**
   * Retry a failed upload
   */
  const retryUpload = useCallback(
    (uploadId: string) => {
      const upload = uploads.find((u) => u.id === uploadId);
      if (upload && upload.status === 'error') {
        // Clear the previous error immediately so the UI resets before the retry
        setUploads((prev) =>
          prev.map((u) => u.id === uploadId ? { ...u, status: 'uploading' as const, error: undefined, progress: 0 } : u)
        );

        // Determine media type from file MIME type, falling back to extension
        const mimeType = upload.file.type.toLowerCase();
        let mediaType: MediaType = MediaType.Image;

        if (mimeType.startsWith('video/')) {
          mediaType = MediaType.Video;
        } else if (mimeType.startsWith('audio/')) {
          mediaType = MediaType.Audio;
        } else if (
          mimeType.includes('gltf') ||
          mimeType.includes('glb') ||
          mimeType.includes('model')
        ) {
          mediaType = MediaType.Model3D;
        } else {
          // MIME type absent or unrecognised (e.g. files extracted from ZIP) — use extension
          mediaType = getMediaTypeFromExtension(getFileExtension(upload.file.name)) ?? MediaType.Image;
        }

        // Defer uploadFile to a separate task so the state-cleared render commits
        // before the upload starts (same pattern as addMissingFiles).
        setTimeout(() => uploadFile(upload, mediaType, null, workspaceId), 0);
      }
    },
    [uploads, uploadFile]
  );

  /**
   * Remove an upload from the queue
   */
  const removeUpload = useCallback((uploadId: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== uploadId));
    abortControllersRef.current.delete(uploadId);
  }, []);

  /**
   * Clear all completed uploads
   */
  const clearCompleted = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'completed'));
  }, []);

  /**
   * Clear all uploads
   */
  const clearAll = useCallback(() => {
    // Cancel all active uploads
    uploads.forEach((upload) => {
      if (upload.status === 'uploading' || upload.status === 'pending') {
        cancelUpload(upload.id);
      }
    });
    setUploads([]);
    abortControllersRef.current.clear();
  }, [uploads, cancelUpload]);

  return {
    uploads,
    addFiles,
    addMissingFiles,
    cancelUpload,
    retryUpload,
    removeUpload,
    clearCompleted,
    clearAll,
  };
};